import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import { ChevronRight, Droplet, HeartPulse, Package, Plus, TrendingUp, X } from 'lucide-react';
import { useState } from 'react';
import { fmtFecha, fmtInt, fmtM3 } from '@/lib/format';
import { dailySummary, sampleSummary } from '@/lib/homeSummary';
import type { CurvaEstandar, EntregaPienso, Pesaje, RegistroDiario } from '@/lib/types';
import { ModalOverlay } from './ModalOverlay';

const metrics = {
  agua: {
    title: 'Agua',
    Icon: Droplet,
    format: (value: number) => fmtM3(value),
    type: 'line',
    colorClass: 'home-color-agua',
  },
  pienso: {
    title: 'Pienso',
    Icon: Package,
    format: (value: number) => `${fmtInt(value)} kg`,
    type: 'bars',
    colorClass: 'home-color-pienso',
  },
  bajas: {
    title: 'Bajas',
    Icon: HeartPulse,
    format: fmtInt,
    type: 'bars',
    colorClass: 'home-color-bajas',
  },
  peso: {
    title: 'Curva',
    Icon: TrendingUp,
    format: (value: number) => `${value > 0 ? '+' : ''}${fmtInt(value)} g`,
    type: 'signed',
    colorClass: 'home-color-peso',
  },
} as const;
type Metric = keyof typeof metrics;
interface Props {
  entrada: string;
  today: string;
  lotes: number[] | undefined;
  registros: RegistroDiario[] | undefined;
  entregas: EntregaPienso[] | undefined;
  pesajes: Pesaje[] | undefined;
  curva: CurvaEstandar | null;
  completos: boolean;
  onFeed?: () => void;
}

/** Un hueco sigue siendo un hueco. Las barras de desviación comparten el cero central. */
function TrendPlot({
  values,
  type,
  large = false,
  formatValue = fmtInt,
  selectedIndex,
}: {
  values: Array<number | null>;
  type: string;
  large?: boolean;
  formatValue?: (value: number) => string;
  selectedIndex?: number;
}) {
  const width = 280,
    height = large ? 132 : 40,
    margin = large ? 18 : 3;
  const limit = Math.max(1, ...values.filter((v): v is number => v != null).map(Math.abs));
  const zero = type === 'signed' ? height / 2 : height - margin;
  const amplitude = type === 'signed' ? height / 2 - margin : height - margin * 2;
  const x = (i: number) => margin + ((i + 0.5) * (width - margin * 2)) / values.length;
  const y = (value: number) => zero - (value / limit) * amplitude;
  let gap = true;
  const path = values
    .map((value, i) => {
      if (value == null) {
        gap = true;
        return '';
      }
      const command = `${gap ? 'M' : 'L'}${x(i)},${y(value)}`;
      gap = false;
      return command;
    })
    .join(' ');
  return (
    <svg
      className={large ? 'home-trend-large' : 'home-spark'}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {selectedIndex != null && (
        <rect
          x={x(selectedIndex) - width / values.length / 2}
          y={0}
          width={width / values.length}
          height={height}
          fill="currentColor"
          opacity={0.08}
        />
      )}
      <line
        x1={margin}
        x2={width - margin}
        y1={zero}
        y2={zero}
        stroke="var(--c-border-strong)"
        strokeWidth={1}
      />
      {type === 'line' ? (
        <>
          <path
            d={path}
            fill="none"
            stroke="currentColor"
            strokeWidth={large ? 2 : 1.5}
            vectorEffect="non-scaling-stroke"
          />
          {values.map((v, i) =>
            v == null ? null : (
              <circle key={i} cx={x(i)} cy={y(v)} r={large ? 3 : 2} fill="currentColor" />
            ),
          )}
        </>
      ) : (
        values.map((v, i) =>
          v == null ? null : (
            <rect
              key={i}
              x={x(i) - (width / values.length) * 0.25}
              width={(width / values.length) * 0.5}
              y={Math.min(zero, y(v))}
              height={Math.max(1, Math.abs(y(v) - zero))}
              rx={2}
              fill="currentColor"
              opacity={v < 0 ? 0.65 : 1}
            />
          ),
        )
      )}
      {large && (
        <>
          <text x={margin} y={13} fill="var(--c-text-secondary)" fontSize={12}>
            {formatValue(limit)}
          </text>
          <text x={margin} y={zero - 3} fill="var(--c-text-secondary)" fontSize={12}>
            0
          </text>
        </>
      )}
    </svg>
  );
}

export function HomeSummary({
  entrada,
  today,
  lotes,
  registros,
  entregas,
  pesajes,
  curva,
  completos,
  onFeed,
}: Props) {
  const [opened, setOpened] = useState<Metric | null>(null);
  const [selectedDate, setSelectedDate] = useState(today);
  const dates = Array.from({ length: 7 }, (_, i) =>
    format(addDays(parseISO(today), i - 6), 'yyyy-MM-dd'),
  ).filter((d) => d >= entrada);
  const forDate = (fecha: string) => {
    const daily = dailySummary(fecha, lotes, registros, entregas);
    const sample = pesajes ? sampleSummary(pesajes, fecha, entrada, curva?.puntos ?? []) : null;
    return { fecha, ...daily, peso: sample?.fecha === fecha ? sample.deltaGramos : null };
  };
  const serie = dates.map(forDate);
  const daily = dailySummary(today, lotes, registros, entregas);
  const sample = pesajes ? sampleSummary(pesajes, today, entrada, curva?.puntos ?? []) : null;
  const current: Record<Metric, number | null> = {
    agua: daily.agua,
    pienso: daily.pienso,
    bajas: daily.bajas,
    peso: sample?.deltaGramos ?? null,
  };
  const detailDates =
    opened === 'peso' && sample && sample.fecha < (dates[0] ?? today)
      ? Array.from(
          { length: differenceInCalendarDays(parseISO(today), parseISO(sample.fecha)) + 1 },
          (_, i) => format(addDays(parseISO(sample.fecha), i), 'yyyy-MM-dd'),
        )
      : dates;
  const detailSeries = detailDates.map(forDate);
  const selected = detailSeries.find((d) => d.fecha === selectedDate);
  const active = opened ? metrics[opened] : null;
  const chartDates = detailDates.length
    ? `${fmtFecha(detailDates[0])} a ${fmtFecha(detailDates.at(-1))}`
    : 'Sin historial';
  const open = (metric: Metric) => {
    setSelectedDate(metric === 'peso' && sample ? sample.fecha : today);
    setOpened(metric);
  };

  return (
    <section className="home-summary" aria-labelledby="home-summary-title">
      <div className="home-summary-head">
        <h2 id="home-summary-title">
          <button className="home-summary-heading" onClick={() => open('bajas')}>
            Resumen de la camada hoy
            <ChevronRight size={14} aria-hidden />
          </button>
        </h2>
        {onFeed && (
          <button
            className="ui-icon-button"
            aria-label="Cargar pienso"
            data-testid="home-cta-cargar-pienso"
            onClick={onFeed}
          >
            <Package size={18} aria-hidden />
            <Plus size={12} aria-hidden />
          </button>
        )}
      </div>
      <div className="home-metrics">
        {(Object.keys(metrics) as Metric[]).map((key) => {
          const { title, Icon, format: formatValue, type, colorClass } = metrics[key];
          return (
            <button
              key={key}
              className={`home-metric ${colorClass}`}
              data-testid={`home-metric-${key}`}
              onClick={() => open(key)}
              aria-label={`Ver evolución de ${key === 'peso' ? 'peso respecto a la curva' : key === 'pienso' ? 'pienso entregado' : title.toLowerCase()}`}
            >
              <span className="home-metric-caption">
                <Icon size={16} aria-hidden />
                <span>{title}</span>
              </span>
              <strong>{current[key] == null ? '—' : formatValue(current[key])}</strong>
              {completos && <TrendPlot values={serie.map((d) => d[key])} type={type} />}
            </button>
          );
        })}
      </div>
      <p className="home-summary-context">
        {daily.bajas == null
          ? `Bajas ${daily.bajasParciales == null ? 'sin actualizar' : daily.partesRegistrados ? `parciales: ${fmtInt(daily.bajasParciales!)}` : 'sin registrar'} · `
          : ''}
        Pienso entregado ·{' '}
        {sample
          ? `Peso: ${fmtFecha(sample.fecha)}${sample.lotes < (lotes?.length ?? 0) ? ` · ${sample.lotes}/${lotes!.length} naves` : ''}`
          : 'Sin muestreo'}
        {daily.agua == null ? ' · Agua sin registro' : ''}
      </p>

      {opened && active && (
        <ModalOverlay
          placement="bottom"
          ariaLabelledby="home-chart-title"
          onBackdrop={() => setOpened(null)}
          className="w-full max-w-md"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 id="home-chart-title" className="text-lg font-semibold">
              {opened === 'peso'
                ? 'Peso frente a la curva'
                : `Evolución de ${active.title.toLowerCase()}`}
            </h2>
            <button
              className="ui-icon-button"
              aria-label="Cerrar resumen"
              onClick={() => setOpened(null)}
            >
              <X size={22} aria-hidden />
            </button>
          </div>
          <p className="text-sm text-text-secondary">{chartDates}</p>
          <label className="mt-4 block" htmlFor="home-chart-date">
            Fecha
          </label>
          <select
            id="home-chart-date"
            className="ui-field mt-1 w-full"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {detailDates.map((date) => (
              <option key={date} value={date}>
                {fmtFecha(date)}
              </option>
            ))}
          </select>
          <div className={`home-chart-detail ${active.colorClass}`}>
            <strong aria-live="polite">
              {selected?.[opened] == null ? 'Sin registro' : active.format(selected[opened])}
            </strong>
            <TrendPlot
              values={detailSeries.map((d) => d[opened])}
              type={active.type}
              large
              formatValue={active.format}
              selectedIndex={detailDates.indexOf(selectedDate)}
            />
            <p className="home-chart-dates">
              <span>{fmtFecha(detailDates[0])}</span>
              <span>{fmtFecha(detailDates.at(-1))}</span>
            </p>
          </div>
          <p className="text-sm text-text-secondary">
            {opened === 'pienso'
              ? 'Entregas por día. No equivale al consumo medido.'
              : opened === 'peso'
                ? `Diferencia entre el muestreo y la curva en su misma fecha. Referencia: ${curva?.origen === 'historico' ? `${curva.camadas_historico} camadas del mismo sexo` : curva?.origen === 'ajustes' ? 'Ajustes' : 'no disponible'}.`
                : opened === 'agua'
                  ? 'Agua anotada en los partes. El contador del sensor es acumulativo.'
                  : 'Muertes y triajes de todas las naves.'}
          </p>
          {opened === 'bajas' && !selected?.completos && (
            <p className="mt-2 text-sm text-warning-text">
              Partes incompletos.{' '}
              {selected?.bajasParciales != null
                ? `${fmtInt(selected.bajasParciales)} bajas registradas hasta ahora.`
                : 'Datos sin actualizar.'}
            </p>
          )}
          <p className="mt-2 text-sm text-text-secondary">Los huecos representan datos ausentes.</p>
          <details className="home-chart-table">
            <summary>Ver valores por fecha</summary>
            <table className="w-full text-sm">
              <caption className="sr-only">Evolución de {active.title.toLowerCase()}</caption>
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">{active.title}</th>
                </tr>
              </thead>
              <tbody>
                {detailSeries.map((d) => (
                  <tr key={d.fecha}>
                    <th scope="row">{fmtFecha(d.fecha)}</th>
                    <td>{d[opened] == null ? 'Sin registro' : active.format(d[opened])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </ModalOverlay>
      )}
    </section>
  );
}
